import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  Heading,
  HStack,
  Radio,
  RadioGroup,
  StackDivider,
  Tab,
  TabList,
  Tabs,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import UserDetails from './UserDetails';
import OrderDetailsCard from '@components/OrderDetailsCard';
import ContentLayout from '@layouts/ContentLayout';
import { useDispatch, useSelector } from 'react-redux';
import usePromise from '@hooks/usePromise';
import {
  clearUserDetails,
  clearUserReferralCode,
  clearUserGomsOrders,
  deleteUser,
  getUserById,
  getUserReferralCode,
  syncWallets
} from '@store/actions/usersActions';
import FullPageSpinner from '@components/FullPageSpinner';
import useIsMobile from '@hooks/useIsMobile';
import useIsAdmin from '@hooks/useIsAdmin';
import useAuthRole from '@hooks/useAuthRole';
import UserTransfers from './UserTransfers';
import { ORDER_PRODUCTS, ORDER_RESOURCE, ROLES } from '@constants';
import useDidMountEffect from '@hooks/useDidMount';
import { MdOutlineAccountBalance } from 'react-icons/md';
import ModalLayout from '@components/Modal/ModalLayout';
import ConfirmModal from '@components/Modal/ConfirmModal';
import { syncVanceCashWallet } from '@store/actions/vanceCashActions';
import { FaUserFriends } from 'react-icons/fa';
import { AiOutlineUserDelete } from 'react-icons/ai';
import { kebabCaseToSpaceSeperate } from '@utils';
const KycV2Details = React.lazy(() => import('./KycV2Details'));
const UserKycTiers = React.lazy(() => import('@views/User/UserKycTiers'));

const User = () => {
  const dispatch = useDispatch();
  const [isSyncingWallet, setIsSyncingWallet] = useState(false);
  const { data: userDetails, isLoading: isLoadingUser, isError } = useSelector(
    (state) => state.users.userDetails
  );
  const isAdmin = useIsAdmin();
  const { hasAnyAuthority } = useAuthRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOrderId = searchParams.get('orderId');
  const initialOrderType = searchParams.get('product');
  const initialOrderResource = searchParams.get('orderResource');
  const { userId } = useParams();
  const [selectedOrder, setSelectedOrder] = useState({
    orderId: initialOrderId || null,
    product: initialOrderType || null,
    orderResource: initialOrderResource || null
  });
  const [activeOrderType, setActiveOrderType] = useState(
    initialOrderType || ORDER_PRODUCTS.REMITTANCE
  );
  const [activeOrderResource, setActiveOrderResource] = useState(
    initialOrderResource || ORDER_RESOURCE.APP_SERVER
  );
  const { isLoading: deleteBtnLoading, run: runDeleteUser } = usePromise();
  const {
    isOpen: isConfirmDeleteOpen,
    onOpen: onConfirmDeleteOpen,
    onClose: onConfirmDeleteClose
  } = useDisclosure();

  const { referralCode: referralCodeData } = useSelector(
    (state) => state.users.userDetails
  );
  const referralCode = useMemo(
    () => referralCodeData?.data?.referralCode,
    [referralCodeData]
  );

  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(getUserById(userId));
    dispatch(getUserReferralCode(userId));
  }, [dispatch, userId]);

  // Load query parameters from URL
  useEffect(() => {
    if (initialOrderResource) {
      setActiveOrderResource(initialOrderResource);
    }
  }, [initialOrderResource]);

  useEffect(() => {
    return () => {
      dispatch(clearUserDetails());
      dispatch(clearUserReferralCode());
      dispatch(clearUserGomsOrders());
    };
  }, []);

  useDidMountEffect(() => {
    setSelectedOrder(null);
  }, [activeOrderType, activeOrderResource]);

  useEffect(() => {
    if (selectedOrder?.orderId) {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set('orderId', selectedOrder.orderId);
      newParams.set(
        'orderResource',
        selectedOrder.orderResource || activeOrderResource
      );
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedOrder, setSearchParams, searchParams, activeOrderResource]);

  const onWalletSync = () => {
    setIsSyncingWallet(true);
    dispatch(
      syncWallets({ userId, onFinish: () => setIsSyncingWallet(false) })
    );
  };

  const handleVanceCashWalletSync = () => {
    setIsSyncingWallet(true);
    dispatch(
      syncVanceCashWallet({
        userId,
        onSuccess: (data) => {
          dispatch(getUserById(userId));
        },
        onComplete: () => {
          setIsSyncingWallet(false);
        }
      })
    );
  };

  const handleDeleteClick = () => {
    onConfirmDeleteOpen();
  };

  const handleDeleteModalConfirm = () => {
    runDeleteUser(
      dispatch(
        deleteUser({
          userId,
          onSuccess: () => {
            dispatch(getUserById(userId));
          },
          onComplete: () => {
            onConfirmDeleteClose();
          }
        })
      )
    );
  };

  const handleOrderResourceChange = (resource) => {
    setActiveOrderResource(resource);
    // Don't update URL to prevent unnecessary re-renders
    // const newParams = new URLSearchParams(searchParams.toString());
    // newParams.set('orderResource', resource);
    // setSearchParams(newParams, { replace: true });
  };

  const handleOrderTypeChange = (orderType) => {
    setActiveOrderType(orderType);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('product', orderType);
    setSearchParams(newParams, { replace: true });
  };

  if (isLoadingUser) return <FullPageSpinner />;

  return (
    <>
      <ModalLayout isOpen={isConfirmDeleteOpen} onClose={onConfirmDeleteClose}>
        <ConfirmModal
          prompt={'Are you sure you want to delete this user ?'}
          handleSubmit={handleDeleteModalConfirm}
          isLoading={deleteBtnLoading}
        />
      </ModalLayout>
      <ContentLayout>
        <Grid gridTemplateColumns={{ base: '1fr', lg: '7fr 0 3fr' }}>
          <VStack
            overflowY={{ base: 'initial', lg: 'auto' }}
            overflowX={'hidden'}
            h={{ lg: '90vh' }}
            py={10}
            px={{ base: 4, sm: 10 }}
            alignItems={'stretch'}
            className="scroll"
            divider={<StackDivider borderColor="whiteAlpha.300" />}
            gap={4}
          >
            {/*Heading*/}
            <HStack width={'100%'} justify={'space-between'}>
              <Heading size="lg" color="colorPrimary">
                User Details
              </Heading>
              {hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.MANAGER_ROLE) && (
                <Button
                  colorScheme={'red'}
                  size="sm"
                  bg="backGround"
                  border={'2px solid'}
                  borderColor={'whiteAlpha.300'}
                  borderRadius={'xl'}
                  p={4}
                  isLoading={deleteBtnLoading}
                  isDisabled={userDetails?.phoneNumber?.endsWith('-delete')}
                  onClick={handleDeleteClick}
                  gap={2}
                >
                  <AiOutlineUserDelete />
                  Delete User
                </Button>
              )}
            </HStack>
            <VStack
              alignItems={userDetails?.kycData ? 'center' : 'flex-start'}
              pb={5}
            >
              <Grid
                gridTemplateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }}
                alignItems={'center'}
                justifyContent={'space-between'}
                width={'100%'}
                gap={6}
              >
                {userDetails && (
                  <UserDetails
                    userData={userDetails}
                    referralCode={referralCode}
                    allowAddFreeTransfer={isAdmin}
                    allowSyncWallet={hasAnyAuthority(
                      ROLES.ADMIN_ROLE,
                      ROLES.MANAGER_ROLE
                    )}
                    onWalletSync={onWalletSync}
                    isSyncingWallet={isSyncingWallet}
                    onVanceCashWalletSync={handleVanceCashWalletSync}
                  />
                )}
                <VStack alignItems={'start'} height={'full'} gap={4}>
                {!isError ? (
                  <Suspense fallback={<Text>Loading...</Text>}>
                    <KycV2Details
                      kycData={userDetails?.userKycs || []}
                      vanceKycData={userDetails?.vanceUserKyc || {}}
                      username={userDetails?.name || ''}
                      userId={userId}
                      country={userDetails?.countryCode}
                    />
                    <UserKycTiers userId={userId} />
                  </Suspense>
                ) : (
                  <Box p={4} border="1px solid" borderColor="whiteAlpha.300" borderRadius="xl">
                    <Text color="whiteAlpha.700">User not found.</Text>
                  </Box>
                )}

                  <Grid
                    templateColumns={{ base: '1fr' }}
                    w={'100%'}
                    gap={4}
                    justifyContent={{ base: 'space-between', xl: 'flex-start' }}
                  >
                    <Link to={`/user/${userId}/source-accounts`}>
                      <Button
                        colorScheme={'brand'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        w={'100%'}
                        size={'sm'}
                        bg={'backGround'}
                        className="scroll"
                        border={'2px solid'}
                        borderColor={'whiteAlpha.300'}
                        borderRadius={'xl'}
                        p={4}
                        gap={2}
                      >
                        <MdOutlineAccountBalance />
                        <Text fontSize={'xs'}>Source Accounts</Text>
                      </Button>
                    </Link>
                    <Link to={`/user/${userId}/beneficiaries`}>
                      <Button
                        colorScheme={'brand'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        w={'100%'}
                        size={'sm'}
                        bg={'backGround'}
                        className="scroll"
                        border={'2px solid'}
                        borderColor={'whiteAlpha.300'}
                        borderRadius={'xl'}
                        p={4}
                        gap={2}
                      >
                        <FaUserFriends />
                        <Text fontSize={'xs'}>Beneficiaries</Text>
                      </Button>
                    </Link>
                  </Grid>
                </VStack>
              </Grid>
            </VStack>

            <VStack alignItems={'stretch'} gap={4}>
              <HStack width={'100%'} justify={'space-between'}>
                <Heading size="md" color="white">
                  Transactions
                </Heading>
              </HStack>

              <VStack gap={2} alignItems={'stretch'}>
                <Tabs
                  variant={'enclosed'}
                  size={'md'}
                  onChange={(e) => {
                    handleOrderTypeChange(Object.keys(ORDER_PRODUCTS)[e]);
                  }}
                  defaultIndex={Object.keys(ORDER_PRODUCTS).indexOf(
                    activeOrderType
                  )}
                >
                  <TabList
                    color={'whiteAlpha.700'}
                    borderColor={'whiteAlpha.500'}
                  >
                    {Object.keys(ORDER_PRODUCTS).map((tabState, ind) => (
                      <Tab
                        key={ind}
                        _selected={{
                          color: 'brand.500',
                          border: '1px solid',
                          borderColor: 'whiteAlpha.500',
                          borderBottom: '1px solid black'
                        }}
                        _focus={{
                          boxShadow: 'none'
                        }}
                      >
                        {kebabCaseToSpaceSeperate(tabState)}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>
              </VStack>

              <VStack gap={2} alignItems={'stretch'}>
                <Text color={'whiteAlpha.700'} fontSize={'sm'} fontWeight={500}>
                  Order Resource
                </Text>
                <RadioGroup
                  value={activeOrderResource}
                  onChange={handleOrderResourceChange}
                  colorScheme="brand"
                >
                  <HStack spacing={6}>
                    <Radio value={ORDER_RESOURCE.APP_SERVER}>
                      <Text color="whiteAlpha.700">App Server Orders</Text>
                    </Radio>
                    <Radio value={ORDER_RESOURCE.GOMS_SERVICE}>
                      <Text color="whiteAlpha.700">Goms Orders</Text>
                    </Radio>
                  </HStack>
                </RadioGroup>
                <Text color="whiteAlpha.600" fontSize="xs" fontStyle="italic">
                  Please also check both services to find your order.
                </Text>
              </VStack>

              {activeOrderType === ORDER_PRODUCTS.REMITTANCE && (
                <UserTransfers
                  userId={userId}
                  setSelectedOrder={setSelectedOrder}
                  orderResource={activeOrderResource}
                  orderType={ORDER_PRODUCTS.REMITTANCE}
                />
              )}

              {activeOrderType === ORDER_PRODUCTS.BBPS && (
                <UserTransfers
                  userId={userId}
                  setSelectedOrder={setSelectedOrder}
                  orderResource={ORDER_RESOURCE.GOMS_SERVICE}
                  orderType={ORDER_PRODUCTS.BBPS}
                />
              )}


            </VStack>
          </VStack>

          <Divider
            style={{ margin: '0' }}
            borderColor="whiteAlpha.300"
            orientation="vertical"
          />

          {!isMobile && (
            <Box m={0} alignSelf={'flex-start'}>
              {selectedOrder?.orderId && (
                <OrderDetailsCard
                  userId={userId}
                  orderId={selectedOrder.orderId}
                  orderResource={selectedOrder.orderResource}
                  isSidebar
                />
              )}
            </Box>
          )}
        </Grid>
      </ContentLayout>
    </>
  );
};

export default User;
